import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Modal,
  Select,
  Spinner,
  useToast,
} from '@adop/ui';
import { Bell, BellOff, Printer, RefreshCw, Search, UtensilsCrossed } from 'lucide-react';
import {
  formatCents,
  formatLocalDateTime,
  ORDER_STATUSES,
} from '@adop/shared';
import type { Json } from '../../lib/database.types';
import type { OrderModifierSnapshot, RefundResponse } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  insertPrintJob,
  listOrderItems,
  listOrders,
  listPrinterDevices,
  subscribeToLocation,
  updateOrderStatus,
  type OrderItemRow,
  type OrderRow,
} from '../../lib/data';
import { orderActionsFor, orderChannelLabel } from '../../lib/order-actions';
import { playNewOrderBeep, unlockAudio } from '../../lib/beep';
import { errorMessage, postJson } from '../../lib/api';
import { humanize, relativeTime } from '../../lib/format';
import { Alert } from '../../components/Alert';
import { PageHeader } from '../../components/PageHeader';
import { OrderStatusBadge, PaymentStatusBadge } from '../../components/StatusBadge';
import { ListSkeleton } from '../../components/LoadingState';

const SOUND_KEY = 'adop.platform.sound';

function parseModifiers(value: Json): OrderModifierSnapshot[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.group !== 'string' || typeof record.option !== 'string') return [];
    return [
      {
        group: record.group,
        option: record.option,
        price_delta_cents: typeof record.price_delta_cents === 'number' ? record.price_delta_cents : 0,
      },
    ];
  });
}

export function OrdersPage() {
  const { location, canManage } = useWorkspace();
  const { toast } = useToast();
  const [status, setStatus] = useState('all');
  const [payment, setPayment] = useState('all');
  const [query, setQuery] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return window.localStorage.getItem(SOUND_KEY) !== 'off';
    } catch {
      return true;
    }
  });
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [items, setItems] = useState<OrderItemRow[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const devices = useAsync(() => (location ? listPrinterDevices(location.id) : Promise.resolve([])), [
    location?.id,
  ]);
  const soundRef = useRef(soundEnabled);
  soundRef.current = soundEnabled;

  const load = useCallback(async () => {
    if (!location) return;
    try {
      setError(null);
      setOrders(await listOrders(location.id));
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    setLoading(true);
    void load();
    if (!location) return;
    const channel = subscribeToLocation(location.id, ['orders'], (table, eventType) => {
      void load();
      if (table === 'orders' && eventType === 'INSERT' && soundRef.current) playNewOrderBeep();
    });
    return () => {
      void channel.unsubscribe();
    };
  }, [location, load]);

  const selected = orders.find((order) => order.id === selectedId) ?? null;

  const selectedOrderId = selected?.id ?? null;

  useEffect(() => {
    if (!selectedOrderId) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    listOrderItems(selectedOrderId)
      .then(setItems)
      .catch((cause: unknown) => setError(errorMessage(cause)))
      .finally(() => setItemsLoading(false));
  }, [selectedOrderId]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return orders.filter((order) => {
      if (status !== 'all' && order.status !== status) return false;
      if (payment !== 'all' && order.payment_method !== payment) return false;
      if (needle.length > 0) {
        const haystack = `${order.order_number} ${order.customer_name}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [orders, status, payment, query]);

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try {
      window.localStorage.setItem(SOUND_KEY, next ? 'on' : 'off');
    } catch {
      // ignore
    }
    if (next) unlockAudio();
  };

  const runAction = async (target: string, extra: Record<string, unknown> = {}) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      await updateOrderStatus(selected.id, target, extra);
      toast({ title: `Order #${selected.order_number} → ${humanize(target)}`, variant: 'success' });
      setCancelOpen(false);
      setCancelReason('');
      await load();
    } catch (cause) {
      toast({ title: 'Could not update order', description: errorMessage(cause), variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const refund = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      await postJson<RefundResponse>(`/api/platform/orders/${selected.id}/refund`, {});
      toast({ title: 'Refund issued', variant: 'success' });
      await load();
    } catch (cause) {
      toast({ title: 'Refund failed', description: errorMessage(cause), variant: 'error' });
    } finally {
      setActionBusy(false);
    }
  };

  const reprint = async () => {
    if (!selected || !location) return;
    const active = devices.data?.find((device) => !device.revoked_at);
    if (!active) {
      toast({
        title: 'No printer paired',
        description: 'Pair a printer first, then you can reprint tickets.',
        variant: 'error',
      });
      return;
    }
    try {
      await insertPrintJob({
        restaurantId: location.restaurant_id,
        locationId: location.id,
        deviceId: active.id,
        kind: 'order',
        orderId: selected.id,
      });
      toast({ title: 'Reprint queued', variant: 'success' });
    } catch (cause) {
      toast({ title: 'Could not queue reprint', description: errorMessage(cause), variant: 'error' });
    }
  };

  if (!location) {
    return <EmptyState icon={<UtensilsCrossed className="h-6 w-6" />} title="Select a location" />;
  }

  const refundable =
    selected?.payment_method === 'card' &&
    (selected.payment_status === 'paid' || selected.payment_status === 'partially_refunded');

  return (
    <div className="space-y-5">
      <PageHeader
        title="Orders"
        description="Live orders for this location. Sound alerts on new orders."
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={toggleSound}>
              {soundEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              {soundEnabled ? 'Sound on' : 'Sound off'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <label className="mb-1 block text-xs font-medium text-zinc-500" htmlFor="order-search">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <Input
                id="order-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Order # or customer"
                className="pl-9"
              />
            </div>
          </div>
          <div className="w-40">
            <Select label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="all">All statuses</option>
              {ORDER_STATUSES.map((entry) => (
                <option key={entry} value={entry}>
                  {humanize(entry)}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <Select label="Payment" value={payment} onChange={(event) => setPayment(event.target.value)}>
              <option value="all">All payments</option>
              <option value="card">Card</option>
              <option value="in_store">Pay in store</option>
            </Select>
          </div>
        </div>
      </Card>

      {loading ? (
        <ListSkeleton />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<UtensilsCrossed className="h-6 w-6" />}
          title="No orders"
          description="New orders will appear here in real time."
        />
      ) : (
        <div className="space-y-2">
          {filtered.map((order) => (
            <button
              key={order.id}
              type="button"
              onClick={() => setSelectedId(order.id)}
              className="flex w-full flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-white p-4 text-left shadow-sm hover:border-zinc-300"
            >
              <span className="w-14 shrink-0 text-lg font-bold text-zinc-900">#{order.order_number}</span>
              <span className="min-w-[8rem] flex-1 text-sm font-medium text-zinc-900">
                {order.customer_name}
              </span>
              <OrderStatusBadge status={order.status} />
              <PaymentStatusBadge status={order.payment_status} />
              <Badge variant={order.order_type === 'dine_in' ? 'info' : 'outline'}>
                {orderChannelLabel(order)}
              </Badge>
              <Badge variant="outline">{order.payment_method === 'card' ? 'Card' : 'In store'}</Badge>
              <span className="text-sm font-semibold text-zinc-900">{formatCents(order.total_cents)}</span>
              <span className="w-20 text-right text-xs text-zinc-500">{relativeTime(order.created_at)}</span>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={selected !== null}
        onClose={() => setSelectedId(null)}
        title={selected ? `Order #${selected.order_number}` : ''}
        size="xl"
        footer={
          selected ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canManage && refundable ? (
                <Button variant="outline" loading={actionBusy} onClick={() => void refund()}>
                  Refund card
                </Button>
              ) : null}
              <Button variant="outline" onClick={() => void reprint()}>
                <Printer className="h-4 w-4" /> Reprint
              </Button>
              {orderActionsFor(selected.status).map((action) => (
                <Button
                  key={action.target}
                  variant={action.tone === 'danger' ? 'danger' : action.tone === 'primary' ? 'primary' : 'secondary'}
                  loading={actionBusy}
                  onClick={() =>
                    action.confirm ? setCancelOpen(true) : void runAction(action.target)
                  }
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : null
        }
      >
        {selected ? (
          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1 text-sm">
                <p className="font-medium text-zinc-900">Customer</p>
                <p className="text-zinc-600">{selected.customer_name}</p>
                {selected.customer_phone ? <p className="text-zinc-600">{selected.customer_phone}</p> : null}
                {selected.customer_email ? <p className="text-zinc-600">{selected.customer_email}</p> : null}
                {selected.notes ? <p className="text-zinc-500">Note: {selected.notes}</p> : null}
              </div>
              <div className="space-y-1 text-sm">
                <p className="font-medium text-zinc-900">Details</p>
                <p className="text-zinc-600">
                  Placed {formatLocalDateTime(selected.created_at, location.timezone)}
                </p>
                <p className="text-zinc-600">
                  Pickup: {selected.pickup_time ? formatLocalDateTime(selected.pickup_time, location.timezone) : 'ASAP'}
                </p>
                <p className="text-zinc-600">Channel: {orderChannelLabel(selected)}</p>
                <p className="text-zinc-600">
                  {selected.payment_method === 'card' ? 'Card' : 'Pay in store'} · {humanize(selected.payment_status)}
                </p>
                {selected.cancelled_reason ? (
                  <p className="text-red-600">Cancelled: {selected.cancelled_reason}</p>
                ) : null}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-200">
              {itemsLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner />
                </div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {items.map((item) => (
                    <li key={item.id} className="flex items-start justify-between gap-4 p-3 text-sm">
                      <div>
                        <p className="font-medium text-zinc-900">
                          {item.quantity}× {item.name}
                        </p>
                        {parseModifiers(item.modifiers).map((modifier, index) => (
                          <p key={index} className="text-xs text-zinc-500">
                            + {modifier.group}: {modifier.option}
                            {modifier.price_delta_cents > 0
                              ? ` (${formatCents(modifier.price_delta_cents)})`
                              : ''}
                          </p>
                        ))}
                        {item.notes ? <p className="text-xs text-amber-700">Note: {item.notes}</p> : null}
                      </div>
                      <span className="font-medium text-zinc-900">{formatCents(item.line_total_cents)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-zinc-600">
                <span>Subtotal</span>
                <span>{formatCents(selected.subtotal_cents)}</span>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>Tax</span>
                <span>{formatCents(selected.tax_cents)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold text-zinc-900">
                <span>Total</span>
                <span>{formatCents(selected.total_cents)}</span>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-zinc-900">Timeline</p>
              <ol className="space-y-1 text-xs text-zinc-500">
                <li>Created · {formatLocalDateTime(selected.created_at, location.timezone)}</li>
                <li>Updated · {relativeTime(selected.updated_at)}</li>
              </ol>
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel order"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>
              Keep order
            </Button>
            <Button
              variant="danger"
              loading={actionBusy}
              onClick={() => void runAction('cancelled', { cancelled_reason: cancelReason.trim() || 'Cancelled by staff' })}
            >
              Cancel order
            </Button>
          </>
        }
      >
        <Input
          label="Reason (optional)"
          value={cancelReason}
          onChange={(event) => setCancelReason(event.target.value)}
          placeholder="Out of ingredients"
        />
      </Modal>
    </div>
  );
}
